import { Card } from "@/components/ui/card";
import { getDealReviewColumns } from "./columns";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnFiltersState,
  SortingState,
  Table as ReactTable,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  VisibilityState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NestedDeal } from "@revelate/types";
import { DataTableFacetedFilter } from "../../deals/components/data-table-faceted-filter";
import { DataTableDateRangeFilter } from "../../deals/components/data-table-daterange-filter";

interface DealReviewDealsTableProps {
  deals: any[];
  onClickDeal?: (dealId: number) => void;
}

export function DealReviewDealsTable({
  deals,
  onClickDeal,
}: DealReviewDealsTableProps) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Hide all extra columns which are only used for filtering
  // NOTE: This is mostly copied from the deals page and is for compatibility with 
  // the components copied from there. The extra columns should just be removed after a refactor
  // and this will no longer be such a long list or perhaps not needed at all.
  const [columnVisibility] =
    useState<VisibilityState>({
      id: false,
      stage_label: false,
      pipeline_label: false,
      deal_type: false,
      onboarding_value: false,
      currency_code: false,
      provider: false,
      provider_id: false,
      pipeline: false,
      is_read_only: false,
      is_closed: false,
      is_won: false,
      is_invoiced: false,
      is_paid: false,
      days_to_close: false,
      created_at: false,
    });

  const table = useReactTable({
    data: deals,
    columns: getDealReviewColumns(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="space-y-4 py-4">
      <DataTableToolbar table={table as ReactTable<NestedDeal>} />

      <Card className="py-2 px-5 border border-gray-300 shadow-sm">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className="px-0 pr-5" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => onClickDeal?.(row.original.id)} className="group cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell className="px-0 pr-5" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


interface DataTableToolbarProps {
  table: ReactTable<NestedDeal>;
}

export function DataTableToolbar({
  table,
}: DataTableToolbarProps) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const uniquePipelines =
    table.getColumn("pipeline_label")
      ? Array.from(table.getColumn("pipeline_label")!.getFacetedUniqueValues().keys())
      : [];

  const pipelineOptions = uniquePipelines.map((p) => ({
    label: p,
    value: p,
  }));

  const uniqueStageLabels =
    table.getColumn("stage_label")
      ? Array.from(table.getColumn("stage_label")!.getFacetedUniqueValues().keys())
      : [];

  const stageLabelOptions = uniqueStageLabels.map((label) => ({
    label: label,
    value: label,
  }));

  const uniqueOwnerNames =
    table.getColumn("owner_name")
      ? Array.from(table.getColumn("owner_name")!.getFacetedUniqueValues().keys())
      : [];

  const ownerOptions = uniqueOwnerNames
    .map((ownerName) => ({
      label: ownerName as string,
      value: ownerName as string,
    }))
    .toSorted((a, b) => {
      if (a.label === "No owner") return -1;
      if (b.label === "No owner") return 1;

      return a.label.localeCompare(b.label);
    });

  return (
    <div className="flex flex-row gap-x-2">
      <div className="flex items-center justify-between">
        {/* Search */}
        <Input
          placeholder="Search"
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="h-8 w-[150px] lg:w-[250px]"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-1 items-center space-x-2">
        {table.getColumn("owner_name") && (
          <DataTableFacetedFilter
            column={table.getColumn("owner_name")}
            title="Owners"
            options={ownerOptions}
          />
        )}
        {table.getColumn("closed_at") && (
          <DataTableDateRangeFilter
            column={table.getColumn("closed_at")}
            title="Close Date"
          />
        )}
        {table.getColumn("pipeline_label") && (
          <DataTableFacetedFilter
            column={table.getColumn("pipeline_label")}
            title="Pipelines"
            options={pipelineOptions}
          />
        )}
        {table.getColumn("stage_label") && (
          <DataTableFacetedFilter
            column={table.getColumn("stage_label")}
            title="Stages"
            options={stageLabelOptions}
          />
        )}
        {isFiltered && (
          <Button
            variant="outline"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            <Cross2Icon className="mr-2 h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
